"use client";

import { Container } from "@pamperedchef/design-system";
import { useTranslations } from "next-intl";
import { useEffect, useLayoutEffect, useState } from "react";

import { BackToPreviousView } from "./BackToPreviousView";
import styles from "./BackToPrevious.module.scss";

export interface BackToPreviousProps {
	/** `Main` translation key for the button label; defaults to `goBack`. */
	label?: string;
}

/**
 * Shows a "go back" affordance only when the visitor navigated here from
 * another page on this site, and hides it again after a back navigation.
 * Not CMS-driven, so templates render it directly rather than through the
 * blocks dispatcher.
 */
export function BackToPrevious({
	label
}: BackToPreviousProps): JSX.Element | null {
	const t = useTranslations("Main");

	const [showBackButton, setShowBackButton] = useState(false);
	const [isBackNavigation, setIsBackNavigation] = useState(false);

	/**
	 * Detects back navigation
	 */
	function handleBackClick(): void {
		window.history.back();
	}

	/**
	 * Triggers after back navigation
	 */
	function handlePopState(): void {
		setIsBackNavigation(true);
	}

	// Use useLayoutEffect so the referrer check resolves before the browser
	// paints. This prevents a layout shift (CLS) where the banner area jumps
	// down ~40 px when BackToPrevious appears after useEffect fires.
	useLayoutEffect(() => {
		const referrer = document.referrer;
		const currentDomain = window.location.origin;

		if (referrer.startsWith(currentDomain)) {
			setShowBackButton(true);
		}
	}, []);

	useEffect(() => {
		window.addEventListener("popstate", handlePopState);

		return (): void => {
			window.removeEventListener("popstate", handlePopState);
		};
	}, []);

	if (isBackNavigation || !showBackButton) {
		return null;
	}

	return (
		<Container containerClassName={styles["container"]}>
			<BackToPreviousView
				label={t(label || "goBack")}
				onClick={handleBackClick}
			/>
		</Container>
	);
}
